import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IconMinus } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { CLOSE_ASSIST_PANEL, selectOpenChats } from '@/reducers/realtime/realtimeChats.redux'

import { ChatPanel } from './ChatPanel'
import { LiveTranscript } from './LiveTranscript'
import { AssistChatsMenu } from './AssistChatsMenu'

import '../realtimeCoaching.scss'
// Chat styling (`.chats`, `.chat-*`, `.assist-*`) lives here; it used to bundle
// via the docked sidebar, which we no longer mount.
import './AlertSidebar/AlertSidebar.scss'

/**
 * Agent Assist — the floating, bottom-right multi-agent coaching workspace that
 * replaces the docked RTC alerts sidebar. The notification bell still carries
 * one-way FYI alerts; live, two-way, call-tied conversations live here, as does
 * the live transcript. Opened from the navbar chat launcher, from a
 * notification's Start listening / Chats action, or from a Live Listen row; the
 * header minimize button hides the whole panel (chats persist — reopen from the
 * launcher), while each agent's chat has its own "End chat" in the header's
 * all-chats menu. The active thread and Listen control are rendered by ChatPanel.
 *
 * The transcript renders as a sibling, not a child: the widget and the
 * transcript open and close independently, so the transcript has to survive the
 * panel being minimized.
 */
export const FloatingCoachingPanel = () => {
  const dispatch = useDispatch()
  const { panelOpen, chats } = useSelector((state) => state.realtimeChats)

  const chatCount = selectOpenChats(chats).length
  let subtitle = 'No active chats'
  if (chatCount === 1) {
    subtitle = '1 active chat'
  } else if (chatCount > 1) {
    subtitle = `${chatCount} active chats`
  }

  return (
    <>
      <LiveTranscript />
      {panelOpen && (
        <div className="floating-coaching-panel">
          <div className="assist-panel" data-testid="assist-panel">
            <div className="assist-panel__header">
              <div className="assist-panel__title">
                <strong>Agent Assist</strong>
                <span>{subtitle}</span>
              </div>
              <div className="assist-panel__header-actions">
                <AssistChatsMenu />
                <Button
                  basic
                  small
                  square
                  className="assist-panel__close"
                  data-testid="assist-close"
                  tooltip="Minimize Agent Assist"
                  onClick={() => dispatch({ type: CLOSE_ASSIST_PANEL })}
                >
                  <IconMinus />
                </Button>
              </div>
            </div>
            <ChatPanel />
          </div>
        </div>
      )}
    </>
  )
}
